/**
 * DatalarBlock.jsx
 *
 * Datalar bloğu — Faz 5 sadeleştirmesi (bkz. proje-yoneticisi görev
 * talimatı, 2026-08-13): eskiden ayrı bir sayfa olan `AdminDanismanDatalar.jsx`
 * (`/danisman/datalar`) buraya taşındı ve tek bir bileşene indirildi.
 * `AdminDanismanCockpit.jsx` (Danışman Paneli, /admin/cockpit) bu bileşeni
 * "Hatırlatmalar" bloğunun ALTINDA render eder — artık ayrı bir sayfa/route
 * DEĞİL. `/danisman/datalar` eski adres App.js'te `/admin/cockpit`'e
 * (query string korunarak) redirect edilir.
 *
 * Sekmeler: Potansiyeller (filtre barı + durum chipleri + grid) / Son
 * Aramalar / Yaklaşan Hatırlatmalar / Data Detay Geçmiş / Öğrencilerim.
 *
 * Son Aramalar, Yaklaşan Hatırlatmalar ve Data Detay Geçmiş sekmeleri
 * `components/crm/DanismanCockpitShared.jsx`'ten gelir (kopyala-yapıştır
 * yapılmadı). Potansiyeller sekmesi backend'in filtre sözleşmesine göre
 * burada yazılıdır (bkz. GET /danisman/cockpit/potansiyeller: q, status,
 * aranmayan, egitim, tarih_tipi, date_from, date_to, meslek, sehir,
 * egitim_durumu, arama_min, arama_max, danisman_id, potansiyel, limit,
 * offset — yanıt: items, total, durum_sayilari, is_truncated).
 *
 * Öğrencilerim sekmesi (bkz. proje-yoneticisi görev talimatı, 2026-08-14 —
 * İş A): GET /danisman/ogrencilerim, sahiplik kısıtlı öğrenci listesi.
 * Potansiyeller'in filtre barı/durum chip'leri buraya KOPYALANMAZ — lead
 * verisi değil öğrenci verisi gösterir, kendi arama kutusu + sıralama
 * seçicisi vardır.
 *
 * Backend prefix: /api/danisman/cockpit, /api/danisman/ogrencilerim
 */

import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { useSearchParams, Link } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { Skeleton } from '../ui/skeleton';
import SmsSendModal from '../admin/SmsSendModal';
import Pagination from './Pagination';
import { useDanismanlarOptions, danismanOptionsList } from '../../hooks/useDanismanlarOptions';
import {
  useLeadStatusOptions,
  leadStatusLabel,
  leadStatusColors,
  StatusBadge,
  SonAramalarTab,
  DataGecmisTab,
  HatirlatmalarPanel,
  LeadDetayModal,
  ErrorRetryCrm,
  TelefonHucre,
  INPUT_CLS,
  LBL_CLS,
  fmtDateTime,
  fmtDate,
} from './DanismanCockpitShared';
import {
  Table2,
  Search,
  AlertTriangle,
  CheckSquare,
  Square,
  MessageSquare,
  Info,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TARIH_TIPI_OPTIONS = [
  { value: 'son_islem', labelTr: 'Son İşlem', labelEn: 'Last Action' },
  { value: 'data_tarihi', labelTr: 'Data Tarihi', labelEn: 'Data Date' },
  { value: 'hatirlatma', labelTr: 'Hatırlatma', labelEn: 'Reminder' },
];

const PAGE_SIZE_OPTIONS = [50, 100, 200];

// Atama durumu / dönüşmüş kayıtlar filtreleri — yalnızca admin/superadmin'e
// görünür (bkz. GET /danisman/cockpit/potansiyeller sözleşmesi, proje-yoneticisi
// görev talimatı 2026-08-14). `atama` danışman rolünde sunucuda zaten yok
// sayılıyor (hepsi kendisine atanmış); `donusmus` varsayılanı `haric` — bu
// varsayılan hem admin hem danışman için backend tarafında da varsayılan.
const ATAMA_OPTIONS = [
  { value: 'tumu', labelTr: 'Tümü', labelEn: 'All' },
  { value: 'atanmis', labelTr: 'Atanmış', labelEn: 'Assigned' },
  { value: 'atanmamis', labelTr: 'Atanmamış', labelEn: 'Unassigned' },
];

const DONUSMUS_OPTIONS = [
  { value: 'haric', labelTr: 'Hariç', labelEn: 'Excluded' },
  { value: 'dahil', labelTr: 'Dahil', labelEn: 'Included' },
  { value: 'sadece', labelTr: 'Sadece', labelEn: 'Only' },
];

// Datalar bloğu her açıldığında (sayfa mount'unda) Potansiyeller sekmesi
// "yeni" (aranmayan) durumundaki dataları varsayılan gösterir (proje-yoneticisi
// talimatı, 2026-08-13: "danışman sayfayı açtığında ilk olarak aranmayan
// datalar listelensin"). Bu bayrak eskiden MODÜL düzeyinde (component
// dışında, mutable let) tutuluyordu — bu, aynı SPA oturumunda yalnızca BİR
// KEZ tetiklenmesine yol açıyordu: kullanıcı Danışman Paneli'nden başka bir
// sayfaya (ör. Ana Sayfa) gidip geri döndüğünde DatalarBlock yeniden mount
// olsa da modül bayrağı hâlâ true kalıyor, varsayılan filtre bir daha
// uygulanmıyordu (ayrıca rol değişimi/çıkış-girişte de yanlış davranırdı).
// Düzeltme: bayrak artık DatalarBlock'un KENDİ `useRef`'i — her DatalarBlock
// mount'unda (yani "Datalar" bloğunun bulunduğu sayfa her açıldığında) TAZE
// `false` ile başlar, PotansiyellerTab'a prop olarak geçirilir. Aynı
// DatalarBlock ÖRNEĞİ içinde sekme değiştirip Potansiyeller'e geri
// dönüldüğünde (PotansiyellerTab yeniden mount olur ama ref DEĞİŞMEZ) bayrak
// hâlâ true'dur — kullanıcının seçtiği filtreye dokunulmaz.

const EMPTY_FILTERS = {
  q: '',
  danismanId: '',
  egitim: '',
  tarihTipi: 'son_islem',
  dateFrom: '',
  dateTo: '',
  meslek: '',
  sehir: '',
  egitimDurumu: '',
  aramaMin: '',
  statusFilter: '',
  potansiyel: false,
  aranmayan: false,
  atama: 'tumu',
  donusmus: 'haric',
};

// ─── Potansiyeller Sekmesi (filtre barı + chip/grid) ─────────────────────────

const PotansiyellerTab = forwardRef(function PotansiyellerTab(
  { lang, tr, authHeaders, statusOptions, danismanlar, isAdmin, onLeadClick, defaultFilterAppliedRef },
  ref
) {
  const toast = useToast();

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const setField = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const [chipStatuses, setChipStatuses] = useState([]);
  const [pageSize, setPageSize] = useState(50);
  const [offset, setOffset] = useState(0);

  // Toplu SMS seçimi — Faz 2/3'te kopan özelliğin geri bağlanması. Sayfa/filtre
  // değişince (yani her fetchLeads çağrısında) sıfırlanır ki yanlış kişiye SMS
  // gitmesin.
  const [selectedIds, setSelectedIds] = useState([]);
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsPhones, setSmsPhones] = useState('');

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [durumSayilari, setDurumSayilari] = useState({});
  const [isTruncated, setIsTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const buildParams = useCallback((override = {}) => {
    const f = { ...filters, ...override.filters };
    const cs = override.chipStatuses !== undefined ? override.chipStatuses : chipStatuses;
    const ps = override.pageSize ?? pageSize;
    const off = override.offset ?? offset;

    const p = new URLSearchParams();
    if (f.q) p.set('q', f.q);
    if (isAdmin && f.danismanId) p.set('danisman_id', f.danismanId);
    if (f.egitim) p.set('egitim', f.egitim);
    p.set('tarih_tipi', f.tarihTipi || 'son_islem');
    if (f.dateFrom) p.set('date_from', f.dateFrom);
    if (f.dateTo) p.set('date_to', f.dateTo);
    if (f.meslek) p.set('meslek', f.meslek);
    if (f.sehir) p.set('sehir', f.sehir);
    if (f.egitimDurumu) p.set('egitim_durumu', f.egitimDurumu);
    if (f.aramaMin !== '' && f.aramaMin !== null && f.aramaMin !== undefined) p.set('arama_min', f.aramaMin);
    if (f.potansiyel) p.set('potansiyel', 'true');
    if (f.aranmayan) p.set('aranmayan', 'true');
    // Atama durumu ve dönüşmüş kayıt filtreleri yalnızca admin/superadmin
    // arayüzünde render edilir (bkz. filtre barı) — danışman rolünde `f.atama`
    // ve `f.donusmus` her zaman EMPTY_FILTERS varsayılanında kalır (backend
    // varsayılanıyla zaten aynı), o yüzden isAdmin dışında da göndersek
    // zararsız olurdu ama gereksiz param'dan kaçınmak için isAdmin ile
    // sınırlanıyor.
    if (isAdmin && f.atama && f.atama !== 'tumu') p.set('atama', f.atama);
    if (isAdmin && f.donusmus && f.donusmus !== 'haric') p.set('donusmus', f.donusmus);

    // status: chip seçimleri (çoklu) > filtre barındaki tekil Durum seçimi
    if (cs.length > 0) p.set('status', cs.join(','));
    else if (f.statusFilter) p.set('status', f.statusFilter);

    p.set('limit', String(ps));
    p.set('offset', String(off));
    return p;
  }, [filters, chipStatuses, pageSize, offset, isAdmin]);

  const fetchLeads = useCallback(async (override = {}) => {
    setLoading(true);
    setError(false);
    // Sayfa/filtre her değiştiğinde (bu fonksiyon her zaman o an çağrılır)
    // toplu SMS seçimini sıfırla — yanlış kişiye SMS gitmesin.
    setSelectedIds([]);
    try {
      const p = buildParams(override);
      const r = await axios.get(`${API}/danisman/cockpit/potansiyeller?${p.toString()}`, {
        headers: authHeaders(),
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setDurumSayilari(r.data.durum_sayilari || {});
      setIsTruncated(!!r.data.is_truncated);
    } catch (err) {
      setError(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildParams, authHeaders, lang]);

  // Kaydet sonrası LeadDetayModal'ın onSaved'i buraya `refetch()` ile ulaşır —
  // mevcut filtreler/sayfa/chip seçimi KORUNARAK (fetchLeads() override'sız
  // çağrılırsa buildParams güncel state'i kullanır) yalnızca veri tazelenir.
  useImperativeHandle(ref, () => ({ refetch: () => fetchLeads() }), [fetchLeads]);

  const allIds = items.map((it) => it.lead_id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : allIds);
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openSmsModal = () => {
    const selectedLeads = items.filter((it) => selectedIds.includes(it.lead_id));
    const phones = selectedLeads
      .map((it) => String(it.telefon || '').replace(/\D/g, ''))
      .filter(Boolean)
      .join('-');
    setSmsPhones(phones);
    setSmsModalOpen(true);
  };

  // İlk yükleme — DatalarBlock'un gerçek ilk mount'unda (bkz.
  // defaultFilterAppliedRef yorumu yukarıda, dosya başı) "yeni" durum chip'i
  // varsayılan aktif gelir; aynı DatalarBlock örneği içinde sekme
  // değiştirip geri dönüldüğünde (bu bileşen yeniden mount olur ama ref
  // DEĞİŞMEZ) filtresiz (mevcut/Tümü) yüklenir.
  useEffect(() => {
    if (!defaultFilterAppliedRef.current) {
      defaultFilterAppliedRef.current = true;
      setChipStatuses(['yeni']);
      fetchLeads({ chipStatuses: ['yeni'], offset: 0 });
    } else {
      fetchLeads({ offset: 0 });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = () => {
    setOffset(0);
    fetchLeads({ offset: 0 });
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setChipStatuses([]);
    setOffset(0);
    fetchLeads({ filters: EMPTY_FILTERS, chipStatuses: [], offset: 0 });
  };

  const toggleChip = (slug) => {
    const next = chipStatuses.includes(slug)
      ? chipStatuses.filter((s) => s !== slug)
      : [...chipStatuses, slug];
    setChipStatuses(next);
    setOffset(0);
    fetchLeads({ chipStatuses: next, offset: 0 });
  };

  const clearChips = () => {
    setChipStatuses([]);
    setOffset(0);
    fetchLeads({ chipStatuses: [], offset: 0 });
  };

  const activeFilterCount = [
    filters.q, filters.danismanId, filters.egitim, filters.dateFrom, filters.dateTo,
    filters.meslek, filters.sehir, filters.egitimDurumu, filters.aramaMin, filters.statusFilter,
    filters.potansiyel || null, filters.aranmayan || null,
    filters.atama !== 'tumu' ? filters.atama : null,
    filters.donusmus !== 'haric' ? filters.donusmus : null,
  ].filter(Boolean).length;

  const activeStatusChips = Object.entries(durumSayilari)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-4">
      {/* ── Filtre barı ── */}
      <div className="card-clay p-4">
        <div className="mb-3">
          <label className={LBL_CLS}>{tr('Datalar içinde ara', 'Search leads')}</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              value={filters.q}
              onChange={(e) => setField('q', e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              placeholder={tr('Ad, Soyad, Cep Telefon, E-mail...', 'Name, Phone, Email...')}
              className={INPUT_CLS + ' pl-9'}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-3">
          {isAdmin && (
            <div>
              <label className={LBL_CLS}>{tr('Danışman', 'Advisor')}</label>
              <select
                value={filters.danismanId}
                onChange={(e) => setField('danismanId', e.target.value)}
                className={INPUT_CLS}
              >
                <option value="">{tr('Tümü', 'All')}</option>
                {/* Filtreleme amaçlı select — pasif danışmanlar da seçilebilir
                    kalır (geçmiş veriyi filtreleyebilmek için), bkz.
                    hooks/useDanismanlarOptions.js dosya başı yorumu. */}
                {danismanOptionsList(danismanlar, tr).map((o) => (
                  <option key={o.key} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className={LBL_CLS}>{tr('Eğitime Göre', 'By Course')}</label>
            <input
              value={filters.egitim}
              onChange={(e) => setField('egitim', e.target.value)}
              placeholder={tr('Eğitim adı...', 'Course name...')}
              className={INPUT_CLS}
            />
          </div>

          <div className="sm:col-span-2">
            <label className={LBL_CLS}>{tr('Tarihe Göre', 'By Date')}</label>
            <div className="flex gap-3 mb-1 flex-wrap">
              {TARIH_TIPI_OPTIONS.map((o) => (
                <label key={o.value} className="flex items-center gap-1 text-xs font-semibold cursor-pointer">
                  <input
                    type="radio"
                    name="tarih_tipi"
                    value={o.value}
                    checked={filters.tarihTipi === o.value}
                    onChange={() => setField('tarihTipi', o.value)}
                    className="accent-[var(--brand-primary)]"
                  />
                  {lang === 'tr' ? o.labelTr : o.labelEn}
                </label>
              ))}
            </div>
            <div className="flex gap-1">
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setField('dateFrom', e.target.value)}
                className={INPUT_CLS + ' text-xs'}
                aria-label={tr('Başlangıç tarihi', 'Start date')}
              />
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setField('dateTo', e.target.value)}
                className={INPUT_CLS + ' text-xs'}
                aria-label={tr('Bitiş tarihi', 'End date')}
              />
            </div>
          </div>

          <div>
            <label className={LBL_CLS}>{tr('Data Durumuna Göre', 'By Lead Status')}</label>
            <select
              value={filters.statusFilter}
              onChange={(e) => setField('statusFilter', e.target.value)}
              className={INPUT_CLS}
            >
              <option value="">{tr('Tümü', 'All')}</option>
              {statusOptions.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={LBL_CLS}>{tr('En Az Arama Sayısı', 'Min Call Count')}</label>
            <input
              type="number"
              min="0"
              value={filters.aramaMin}
              onChange={(e) => setField('aramaMin', e.target.value)}
              placeholder="0"
              className={INPUT_CLS}
            />
          </div>

          <div>
            <label className={LBL_CLS}>{tr('Meslek', 'Profession')}</label>
            <input
              value={filters.meslek}
              onChange={(e) => setField('meslek', e.target.value)}
              placeholder={tr('Meslek...', 'Profession...')}
              className={INPUT_CLS}
            />
          </div>

          <div>
            <label className={LBL_CLS}>{tr('Şehir', 'City')}</label>
            <input
              value={filters.sehir}
              onChange={(e) => setField('sehir', e.target.value)}
              placeholder={tr('Şehir...', 'City...')}
              className={INPUT_CLS}
            />
          </div>

          <div>
            <label className={LBL_CLS}>{tr('Eğitim Durumu', 'Education Level')}</label>
            <input
              value={filters.egitimDurumu}
              onChange={(e) => setField('egitimDurumu', e.target.value)}
              placeholder={tr('Eğitim durumu...', 'Education level...')}
              className={INPUT_CLS}
            />
          </div>

          {/* Atama durumu ve dönüşmüş kayıt filtreleri — yalnızca admin/
              superadmin görür (bkz. dosya başı ATAMA_OPTIONS/DONUSMUS_OPTIONS
              yorumu). Danışman rolünde bu iki select DOM'a hiç girmez. */}
          {isAdmin && (
            <div>
              <label htmlFor="datalar-atama-durumu" className={LBL_CLS}>{tr('Atama Durumu', 'Assignment')}</label>
              <select
                id="datalar-atama-durumu"
                value={filters.atama}
                onChange={(e) => setField('atama', e.target.value)}
                className={INPUT_CLS}
              >
                {ATAMA_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{lang === 'tr' ? o.labelTr : o.labelEn}</option>
                ))}
              </select>
            </div>
          )}

          {isAdmin && (
            <div>
              <label htmlFor="datalar-donusmus" className={LBL_CLS}>{tr('Dönüşmüşler', 'Converted')}</label>
              <select
                id="datalar-donusmus"
                value={filters.donusmus}
                onChange={(e) => setField('donusmus', e.target.value)}
                className={INPUT_CLS}
              >
                {DONUSMUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{lang === 'tr' ? o.labelTr : o.labelEn}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {filters.donusmus === 'haric' && (
          <p
            className="flex items-start gap-2 text-xs font-semibold p-2 rounded-lg mb-3"
            style={{ backgroundColor: 'rgba(46,107,143,0.08)', border: '1px solid rgba(46,107,143,0.25)', color: 'var(--crm-info)' }}
          >
            <Info size={13} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span>
              {tr(
                'Öğrenciye dönüşmüş kayıtlar bu listede gösterilmez — "Öğrencilerim" sekmesinden görüntülenebilir.',
                'Leads that have converted to students are not shown in this list — view them under the "My Students" tab.'
              )}
            </span>
          </p>
        )}

        <div className="flex items-center gap-3 flex-wrap mb-3">
          <label className="flex items-center gap-1.5 text-xs font-bold cursor-pointer px-3 py-1.5 rounded-xl border-2 border-[var(--border-light)]">
            <input
              type="checkbox"
              checked={filters.potansiyel}
              onChange={(e) => setField('potansiyel', e.target.checked)}
              className="accent-[var(--brand-primary)]"
            />
            {tr('Potansiyel Olanların Tümü', 'All Potential Leads')}
          </label>
          <label className="flex items-center gap-1.5 text-xs font-bold cursor-pointer px-3 py-1.5 rounded-xl border-2 border-[var(--border-light)]">
            <input
              type="checkbox"
              checked={filters.aranmayan}
              onChange={(e) => setField('aranmayan', e.target.checked)}
              className="accent-[var(--brand-primary)]"
            />
            {tr('Aranmayan Data', 'Uncalled Leads')}
          </label>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={applyFilters}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-emerald-500 text-white text-sm font-bold border-2 border-emerald-600 hover:bg-emerald-600 disabled:opacity-50"
          >
            <Search size={15} />
            {tr('Listele', 'List')}
          </button>
          <button
            onClick={clearFilters}
            className="px-4 py-2 rounded-xl border-2 border-[var(--border-light)] text-sm font-semibold text-[var(--text-muted)] hover:bg-gray-50"
          >
            {tr('Filtreleri Temizle', 'Clear Filters')}
          </button>
          {activeFilterCount > 0 && (
            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-[var(--brand-primary)] text-white">
              {activeFilterCount} {tr('aktif filtre', 'active filters')}
            </span>
          )}
        </div>
      </div>

      {/* ── Durum chip satırı ── */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={clearChips}
          className={`px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-all ${
            chipStatuses.length === 0
              ? 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary)] ring-2 ring-offset-1 ring-[var(--brand-primary)]'
              : 'bg-white border-[var(--border-light)] text-[var(--text-main)] hover:bg-gray-50'
          }`}
        >
          {tr('Tümü', 'All')} ({total})
        </button>
        {activeStatusChips.map(([slug, count]) => {
          const { bg, text, border } = leadStatusColors(slug);
          const lbl = leadStatusLabel(slug, statusOptions);
          const active = chipStatuses.includes(slug);
          return (
            <button
              key={slug}
              onClick={() => toggleChip(slug)}
              aria-pressed={active}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-all ${bg} ${text} ${border} ${
                active ? 'ring-2 ring-offset-1 ring-[var(--crm-navy)]' : 'opacity-70 hover:opacity-100'
              }`}
            >
              {lbl} ({count})
            </button>
          );
        })}
      </div>

      {isTruncated && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border-2 border-amber-200 rounded-xl">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0" />
          <p className="text-sm font-bold text-amber-700">
            {tr('Sonuçlar kırpıldı — daha kesin sonuç için filtreleri daraltın.', 'Results were truncated — narrow your filters for more precise results.')}
          </p>
        </div>
      )}

      {/* ── Grid ── */}
      <div className="card-clay overflow-hidden">
        {loading && (
          <div className="p-4 space-y-2">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-11 w-full" />)}
          </div>
        )}
        {!loading && error && <div className="p-4"><ErrorRetryCrm onRetry={() => fetchLeads()} tr={tr} /></div>}
        {!loading && !error && (
          <>
            {/* Toplu SMS eylem şeridi */}
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 border-b-2 border-amber-200">
                <span className="text-sm font-bold text-amber-700">
                  {selectedIds.length} {tr('seçili', 'selected')}
                </span>
                <button
                  onClick={openSmsModal}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-emerald-500 text-white text-xs font-bold border-2 border-emerald-600 hover:bg-emerald-600"
                >
                  <MessageSquare size={13} />
                  {tr('Seçililere SMS Gönder', 'Send SMS to Selected')}
                </button>
                <button
                  onClick={() => setSelectedIds([])}
                  className="text-xs text-amber-600 font-bold hover:underline"
                >
                  {tr('İptal', 'Cancel')}
                </button>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="p-3 w-8">
                      <button
                        onClick={toggleAll}
                        aria-label={tr('Tümünü seç', 'Select all')}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {allSelected ? <CheckSquare size={16} className="text-[var(--brand-primary)]" /> : <Square size={16} />}
                      </button>
                    </th>
                    <th scope="col" className="text-left p-3 font-bold">#</th>
                    <th scope="col" className="text-left p-3 font-bold">{tr('Ad Soyad', 'Name')}</th>
                    <th scope="col" className="text-left p-3 font-bold">{tr('Telefon', 'Phone')}</th>
                    <th scope="col" className="text-center p-3 font-bold">{tr('Durum', 'Status')}</th>
                    <th scope="col" className="text-center p-3 font-bold">{tr('Arama Sayısı', 'Calls')}</th>
                    <th scope="col" className="text-left p-3 font-bold">{tr('Son İşlem Tarihi', 'Last Action')}</th>
                    <th scope="col" className="text-left p-3 font-bold">{tr('Data Tarihi', 'Data Date')}</th>
                    <th scope="col" className="text-left p-3 font-bold">{tr('Danışman', 'Advisor')}</th>
                    <th scope="col" className="text-left p-3 font-bold">{tr('Eğitim', 'Course')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const isSel = selectedIds.includes(it.lead_id);
                    return (
                      <tr
                        key={it.lead_id}
                        className={`border-t border-[var(--border-light)] hover:bg-gray-50 ${isSel ? 'bg-blue-50' : ''}`}
                      >
                        <td className="p-3">
                          <button
                            onClick={() => toggleOne(it.lead_id)}
                            aria-label={tr('Seç', 'Select')}
                            className="text-gray-400 hover:text-[var(--brand-primary)]"
                          >
                            {isSel ? <CheckSquare size={16} className="text-[var(--brand-primary)]" /> : <Square size={16} />}
                          </button>
                        </td>
                        <td className="p-3 text-xs text-[var(--text-muted)]">{offset + idx + 1}</td>
                        <td className="p-3">
                          <button
                            onClick={() => onLeadClick({ lead_id: it.lead_id, ad_soyad: it.ad_soyad })}
                            className="font-bold text-[var(--brand-primary)] hover:underline text-left"
                          >
                            {it.ad_soyad}
                          </button>
                        </td>
                        <td className="p-3">
                          <TelefonHucre telefon={it.telefon} tr={tr} />
                        </td>
                        <td className="p-3 text-center">
                          <StatusBadge status={it.status} options={statusOptions} />
                        </td>
                        <td className="p-3 text-center text-sm font-bold">{it.arama_sayisi ?? 0}</td>
                        <td className="p-3 text-xs">{fmtDateTime(it.son_islem_tarihi)}</td>
                        <td className="p-3 text-xs">{fmtDateTime(it.data_tarihi)}</td>
                        <td className="p-3 text-xs">{it.danisman_adi || '-'}</td>
                        <td className="p-3 text-xs">{it.egitim || '-'}</td>
                      </tr>
                    );
                  })}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={10} className="p-6 text-center text-sm text-[var(--text-muted)]">
                        {tr('Kayıt yok', 'No records found')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              total={total}
              limit={pageSize}
              offset={offset}
              loading={loading}
              tr={tr}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageChange={(o) => { setOffset(o); fetchLeads({ offset: o }); }}
              onLimitChange={(s) => { setPageSize(s); setOffset(0); fetchLeads({ pageSize: s, offset: 0 }); }}
            />
          </>
        )}
      </div>

      {/* Toplu SMS modalı */}
      <SmsSendModal
        open={smsModalOpen}
        onOpenChange={setSmsModalOpen}
        defaultPhone={smsPhones}
      />
    </div>
  );
});

// ─── Öğrencilerim Sekmesi (bkz. proje-yoneticisi görev talimatı, 2026-08-14
// — İş A) ──────────────────────────────────────────────────────────────────
//
// GET /danisman/ogrencilerim — sahiplik kısıtlı öğrenci listesi (bkz.
// backend/routes/danisman_cockpit.py:1782). Bu sekme lead değil ÖĞRENCİ
// verisi gösterdiği için Potansiyeller'in filtre barı/durum chip'leri
// buraya KOPYALANMAZ — yalnızca kendi arama kutusu + sıralama seçici var.
//
// Rol davranışı: danisman rolünde `danisman_id` backend'de zaten yok
// sayılır (yalnızca kendi öğrencileri döner) — burada admin için ayrı bir
// danışman filtresi eklenmedi (görev talimatı yalnızca arama + sıralama
// istedi); admin `danisman_id` göndermeden çağırdığında backend TÜM
// öğrencileri döner.
const OGRENCILERIM_SORT_OPTIONS = [
  { value: 'kayit_tarihi_desc', labelTr: 'Kayıt Tarihi (Yeni → Eski)', labelEn: 'Registration Date (New → Old)' },
  { value: 'kayit_tarihi_asc', labelTr: 'Kayıt Tarihi (Eski → Yeni)', labelEn: 'Registration Date (Old → New)' },
  { value: 'ad_soyad_asc', labelTr: 'Ad Soyad (A → Z)', labelEn: 'Name (A → Z)' },
  { value: 'ad_soyad_desc', labelTr: 'Ad Soyad (Z → A)', labelEn: 'Name (Z → A)' },
];

const OGRENCILERIM_PAGE_SIZE_OPTIONS = [25, 50, 100];

function OdemeDurumuBadge({ durum, tr }) {
  // Renk TEK bilgi taşıyıcısı değil — metin etiketi her zaman görünür
  // (bkz. proje-yoneticisi görev talimatı erişilebilirlik notu).
  const map = {
    borc_yok: { cls: 'bg-gray-100 text-gray-600 border-gray-200', label: tr('Borç yok', 'No debt') },
    acik: { cls: 'bg-amber-100 text-amber-700 border-amber-200', label: tr('Açık', 'Open') },
    odendi: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: tr('Ödendi', 'Paid') },
  };
  const { cls, label } = map[durum] || { cls: 'bg-gray-100 text-gray-600 border-gray-200', label: durum || '-' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${cls}`}>
      {label}
    </span>
  );
}

const OgrencilerimTab = forwardRef(function OgrencilerimTab({ lang, tr, authHeaders, isAdmin }, ref) {
  const toast = useToast();

  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [sort, setSort] = useState('kayit_tarihi_desc');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(50);

  const fetchData = useCallback(async (override = {}) => {
    setLoading(true);
    setError(false);
    try {
      const p = new URLSearchParams();
      const qq = override.q !== undefined ? override.q : q;
      if (qq) p.set('q', qq);
      p.set('sort', override.sort ?? sort);
      p.set('limit', String(override.limit ?? limit));
      p.set('offset', String(override.offset ?? offset));
      const r = await axios.get(`${API}/danisman/ogrencilerim?${p.toString()}`, {
        headers: authHeaders(),
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      setError(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, sort, limit, offset, authHeaders, lang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useImperativeHandle(ref, () => ({ refetch: () => fetchData() }), [fetchData]);

  const applySearch = () => {
    setQ(qInput);
    setOffset(0);
    fetchData({ q: qInput, offset: 0 });
  };

  const changeSort = (val) => {
    setSort(val);
    setOffset(0);
    fetchData({ sort: val, offset: 0 });
  };

  return (
    <div className="space-y-4">
      <div className="card-clay p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <label className={LBL_CLS}>{tr('Öğrenci ara', 'Search students')}</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              placeholder={tr('Ad, Soyad, Telefon, E-mail...', 'Name, Phone, Email...')}
              className={INPUT_CLS + ' pl-9'}
            />
          </div>
        </div>
        <div>
          <label className={LBL_CLS}>{tr('Sırala', 'Sort')}</label>
          <select
            value={sort}
            onChange={(e) => changeSort(e.target.value)}
            className={INPUT_CLS}
          >
            {OGRENCILERIM_SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{lang === 'tr' ? o.labelTr : o.labelEn}</option>
            ))}
          </select>
        </div>
        <button
          onClick={applySearch}
          disabled={loading}
          className="flex items-center gap-2 px-5 py-2 rounded-xl bg-emerald-500 text-white text-sm font-bold border-2 border-emerald-600 hover:bg-emerald-600 disabled:opacity-50"
        >
          <Search size={15} />
          {tr('Ara', 'Search')}
        </button>
      </div>

      <div className="card-clay overflow-hidden">
        {loading && (
          <div className="p-4 space-y-2">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-11 w-full" />)}
          </div>
        )}
        {!loading && error && <div className="p-4"><ErrorRetryCrm onRetry={() => fetchData()} tr={tr} /></div>}
        {!loading && !error && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="text-left p-3 font-bold">{tr('Ad Soyad', 'Name')}</th>
                    <th scope="col" className="text-left p-3 font-bold">{tr('Öğrenci No', 'Student No')}</th>
                    <th scope="col" className="text-left p-3 font-bold">{tr('İletişim', 'Contact')}</th>
                    <th scope="col" className="text-left p-3 font-bold">{tr('Kayıt Tarihi', 'Registration Date')}</th>
                    <th scope="col" className="text-center p-3 font-bold">{tr('Aktif Kurs', 'Active Courses')}</th>
                    <th scope="col" className="text-center p-3 font-bold">{tr('Ödeme Durumu', 'Payment Status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.user_id} className="border-t border-[var(--border-light)] hover:bg-gray-50">
                      <td className="p-3">
                        {/* Danışmana özel sade öğrenci detay ekranı (bkz.
                            proje-yoneticisi görev talimatı, 2026-08-14 —
                            İş B) — /admin/students/{id} (admin-only tam
                            yönetici görünümü) DEĞİL, DanismanOgrenciDetay.jsx
                            dosya başı yorumuna bkz. Admin rolü de buraya
                            gelir (sade görünüm) ama sayfa üstündeki "Tam
                            yönetici görünümü" linkiyle admin tam sayfaya
                            erişimini korur. */}
                        <Link
                          to={`/danisman/ogrenci/${it.user_id}`}
                          className="font-bold text-[var(--brand-primary)] hover:underline"
                        >
                          {it.ad_soyad || '-'}
                        </Link>
                      </td>
                      <td className="p-3 text-xs text-[var(--text-muted)]">{it.ogrenci_no || '-'}</td>
                      <td className="p-3">
                        <div className="space-y-0.5">
                          <div className="text-xs">{it.email || '-'}</div>
                          <TelefonHucre telefon={it.telefon} tr={tr} />
                        </div>
                      </td>
                      <td className="p-3 text-xs">{fmtDate(it.kayit_tarihi)}</td>
                      <td className="p-3 text-center text-sm font-bold">{it.aktif_kurs_sayisi ?? 0}</td>
                      <td className="p-3 text-center">
                        <OdemeDurumuBadge durum={it.son_odeme_durumu} tr={tr} />
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-sm text-[var(--text-muted)]">
                        {isAdmin
                          ? tr('Kayıt yok', 'No records found')
                          : tr(
                              'Size atanmış öğrenci bulunamadı. Yalnızca sizin lead\'inizden dönüşen veya satışını yaptığınız öğrenciler burada listelenir.',
                              'No students are assigned to you. Only students converted from your leads or sold by you appear here.'
                            )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              total={total}
              limit={limit}
              offset={offset}
              loading={loading}
              tr={tr}
              pageSizeOptions={OGRENCILERIM_PAGE_SIZE_OPTIONS}
              onPageChange={(o) => { setOffset(o); fetchData({ offset: o }); }}
              onLimitChange={(s) => { setLimit(s); setOffset(0); fetchData({ limit: s, offset: 0 }); }}
            />
          </>
        )}
      </div>
    </div>
  );
});

// ─── Datalar bloğu (Danışman Paneli'nin altında render edilir) ──────────────

const DatalarBlock = forwardRef(function DatalarBlock({ lang, tr, user, authHeaders, onLeadSaved }, ref) {
  const { options: statusOptions } = useLeadStatusOptions();
  const [searchParams] = useSearchParams();

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // ?tab=data-gecmis&lead_id=... ile derin bağlantı — LeadDetayModal'daki
  // "Data Geçmişi" linki yeni sekmede /admin/cockpit?tab=...&lead_id=... açar.
  const initialTab = searchParams.get('tab');
  const initialLeadId = searchParams.get('lead_id') || '';

  const OGRENCILERIM_TAB_ID = 'ogrencilerim';
  const [activeTab, setActiveTab] = useState(
    initialTab && ['potansiyeller', 'son-aramalar', 'yaklasan-hatirlatmalar', 'data-gecmis', OGRENCILERIM_TAB_ID].includes(initialTab)
      ? initialTab
      : 'potansiyeller'
  );
  // Filtreleme amaçlı danışman listesi — HER ZAMAN aktif=tumu (bkz.
  // hooks/useDanismanlarOptions.js). Yalnızca admin ise çekilir (danışman
  // rolünde filtre barında bu select zaten render edilmez).
  const { danismanlar } = useDanismanlarOptions(authHeaders, { includeAdmins: true, enabled: isAdmin });
  const [gorusmeLead, setGorusmeLead] = useState(null);

  // Lead detayı — TEK giriş noktası (LeadDetayModal, ortada Dialog).
  const openLeadDetay = (lead) => setGorusmeLead(lead);

  // Kaydet sonrası aktif sekmeyi tazelemek için — her sekme bileşeni
  // forwardRef ile `refetch()` dışa veriyor (bkz. DanismanCockpitShared).
  // Aynı anda tek sekme mount olduğu için TEK bir ref yeterli; sekme
  // değişince React ref'i otomatik olarak yeni bileşene bağlar.
  const activeTabRef = useRef(null);

  // Bu DatalarBlock ÖRNEĞİNE özel "varsayılan filtre uygulandı mı" bayrağı —
  // her DatalarBlock mount'unda (yani bu blok içeren sayfa her açıldığında)
  // useRef(false) TAZE başlar; sekme değiştirip Potansiyeller'e geri
  // dönüldüğünde PotansiyellerTab yeniden mount olsa da bu ref (ve içindeki
  // .current) DEĞİŞMEZ (bkz. PotansiyellerTab üstündeki dosya başı yorumu).
  const defaultFilterAppliedRef = useRef(false);

  const handleLeadSaved = () => {
    activeTabRef.current?.refetch();
    // Cockpit'teki Hatırlatmalar bloğu ile Datalar bloğu artık aynı sayfada
    // ama bağımsız veri kaynakları — bu bloktaki modalden kaydedince üst
    // bileşen (AdminDanismanCockpit) de kendi bloğunu (Hatırlatmalar +
    // özet) tazeleyebilsin diye bildirim yapılır.
    onLeadSaved?.();
  };

  // Üst bileşenin (AdminDanismanCockpit) Hatırlatmalar modalinden kaydedince
  // bu bloğu da tazeleyebilmesi için dışa `refetch()` verilir — aynı
  // forwardRef + useImperativeHandle deseni.
  useImperativeHandle(ref, () => ({ refetch: () => activeTabRef.current?.refetch() }), []);

  const TABS = [
    { id: 'potansiyeller', labelTr: 'Potansiyeller', labelEn: 'Leads' },
    { id: 'son-aramalar', labelTr: 'Son Aramalar', labelEn: 'Recent Calls' },
    { id: 'yaklasan-hatirlatmalar', labelTr: 'Yaklaşan Hatırlatmalar', labelEn: 'Upcoming Reminders' },
    { id: 'data-gecmis', labelTr: 'Data Detay Geçmiş', labelEn: 'History' },
    { id: OGRENCILERIM_TAB_ID, labelTr: 'Öğrencilerim', labelEn: 'My Students' },
  ];

  return (
    <div>
      {/* Bölüm başlığı — Datalar artık ayrı bir sayfa değil, Danışman
          Paneli'nin altında yaşayan bir blok. */}
      <div className="flex items-center gap-2 mb-4 pt-5 mt-5 border-t-2" style={{ borderColor: 'var(--crm-card-border)' }}>
        <Table2 size={18} style={{ color: 'var(--crm-navy)' }} aria-hidden="true" />
        <h2 className="font-display text-lg font-bold" style={{ color: 'var(--crm-navy-text)' }}>
          {tr('Datalar', 'Data')}
        </h2>
      </div>

      {/* Sekmeler */}
      <div className="flex gap-1 mb-4 border-b-2 overflow-x-auto" style={{ borderColor: 'var(--crm-card-border)' }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-bold rounded-t-xl transition-all border-b-2 -mb-0.5 whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-white'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-white/50'
            }`}
            style={activeTab === tab.id ? { borderColor: 'var(--crm-navy)', color: 'var(--crm-navy)' } : undefined}
          >
            {lang === 'tr' ? tab.labelTr : tab.labelEn}
          </button>
        ))}
      </div>

      {activeTab === 'potansiyeller' && (
        <PotansiyellerTab
          ref={activeTabRef}
          lang={lang}
          tr={tr}
          authHeaders={authHeaders}
          statusOptions={statusOptions}
          danismanlar={danismanlar}
          isAdmin={isAdmin}
          onLeadClick={openLeadDetay}
          defaultFilterAppliedRef={defaultFilterAppliedRef}
        />
      )}
      {activeTab === 'son-aramalar' && (
        <SonAramalarTab
          ref={activeTabRef}
          lang={lang}
          authHeaders={authHeaders}
          statusOptions={statusOptions}
          onLeadClick={openLeadDetay}
        />
      )}
      {activeTab === 'yaklasan-hatirlatmalar' && (
        <div className="card-clay overflow-hidden">
          <HatirlatmalarPanel
            ref={activeTabRef}
            scope="yaklasan"
            variant="list"
            limit={50}
            lang={lang}
            authHeaders={authHeaders}
            isAdmin={isAdmin}
            onLeadClick={openLeadDetay}
            statusOptions={statusOptions}
            emptyText={tr('Yaklaşan hatırlatma yok', 'No upcoming reminders')}
          />
        </div>
      )}
      {activeTab === 'data-gecmis' && (
        <DataGecmisTab
          ref={activeTabRef}
          lang={lang}
          authHeaders={authHeaders}
          statusOptions={statusOptions}
          onLeadClick={openLeadDetay}
          initialLeadId={initialLeadId}
        />
      )}
      {activeTab === OGRENCILERIM_TAB_ID && (
        <OgrencilerimTab
          ref={activeTabRef}
          lang={lang}
          tr={tr}
          authHeaders={authHeaders}
          isAdmin={isAdmin}
        />
      )}

      {/* Lead detay modalı */}
      <LeadDetayModal
        lead={gorusmeLead}
        lang={lang}
        statusOptions={statusOptions}
        authHeaders={authHeaders}
        isAdmin={isAdmin}
        currentUserId={user?.user_id}
        onClose={() => setGorusmeLead(null)}
        onSaved={handleLeadSaved}
      />
    </div>
  );
});

export default DatalarBlock;
